import styled from '@emotion/styled';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { queryClient } from '@hotjar/webapp/utils/react-query';
import { USER_RESOURCES_QUERY_KEY } from '@hotjar/user-resources';
import type { Site } from '@hotjar/site';
import { useSiteName } from '@hotjar/site';
import {
  bus,
  trackingCodeInstalled,
  trackingCodeModalOpened,
  trackingCodeVerificationFinished,
} from '@hotjar/webapp/utils/events';
import { tokens, Modal, Button, CloseAction, Icon } from '@hotjar/hotsauce';
import { useTrackingCode } from '@hotjar/webapp/hooks/useTrackingCode';
import { useHelpHotjarUrl } from '@hotjar/translations';
import type { TrackingCodeTabsProps } from './TrackingCodeTabs';
import { TrackingCodeTabs } from './TrackingCodeTabs';

const CloseButton = styled(CloseAction)`
  color: ${tokens.color.white};
  position: absolute;
  top: ${tokens.spacer.x3};
  right: ${tokens.spacer.x3};

  &:hover {
    color: ${tokens.color.white};
  }
`;

const Body = styled(Modal.Body)`
  padding: 0;
`;

const Footer = styled(Modal.Footer)`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  border-top: 1px solid ${tokens.color.ui_border};
`;

const GetHelpButton = styled(Button)`
  margin-left: ${tokens.spacer.x1};
`;

const Header = styled.div`
  padding: ${tokens.spacer.x3};
  color: ${tokens.color.white};
  ${tokens.text('subtitle2')};
  border-radius: ${tokens.radius.m} ${tokens.radius.m} 0 0;
  display: flex;
  align-items: center;
  gap: ${tokens.spacer.x0};
`;

const SuccessHeader = styled(Header)`
  background-color: ${tokens.color.mint_600};
`;

const WarningHeader = styled(Header)`
  background-color: ${tokens.color.ember_600};
`;

export const TrackingCodeModal = (): JSX.Element | null => {
  const { t } = useTranslation();
  const helpHotjarUrl = useHelpHotjarUrl();
  const { getState } = useTrackingCode();
  const [site, setSite] = useState(undefined as unknown as Site);
  const [activeTab, setActiveTab] = useState(
    undefined as unknown as TrackingCodeTabsProps['activeTab'],
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const siteName = useSiteName(site);
  const state = getState(site?.lastData);
  const lastActiveHoursAgo =
    state.lastData.hoursSince !== null ? Math.max(1, state.lastData.hoursSince) : null;

  const handleSuccessClick = () => {
    setIsModalOpen(false);
    bus.publish(trackingCodeVerificationFinished({ site }));
  };

  useEffect(() => {
    const unsubscribe = bus.subscribe(trackingCodeInstalled, async () => {
      await queryClient.invalidateQueries(USER_RESOURCES_QUERY_KEY);
      setIsModalOpen(false);
    });

    return () => {
      unsubscribe();
    };
  }, [setIsModalOpen]);

  useEffect(() => {
    const unsubscribe = bus.subscribe(trackingCodeModalOpened, ({ payload }) => {
      if (payload.site) {
        setSite(payload.site);
        setActiveTab(payload.activeTab);
        setIsModalOpen(true);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [setSite, setActiveTab, setIsModalOpen]);

  return site ? (
    <Modal
      contentLabel="Tracking Code"
      isOpen={isModalOpen}
      onClose={() => setIsModalOpen(false)}
      hideCloseButton
      size="l"
    >
      <CloseButton description="Close Modal" onClick={() => setIsModalOpen(false)} />
      <Body>
        {state.isActive ? (
          <SuccessHeader>
            <Icon icon="check_alt" size="m" />{' '}
            {t('activation:tracking-code.active-since', { siteName, lastActiveHoursAgo })}
          </SuccessHeader>
        ) : (
          <WarningHeader>
            <Icon icon="warning_alt" size="m" />{' '}
            {t('activation:tracking-code.inactive-24h', { siteName })}
          </WarningHeader>
        )}
        <TrackingCodeTabs site={site} activeTab={activeTab} onSuccessClick={handleSuccessClick} />
      </Body>
      <Footer>
        {t('activation:tracking-code.need-help-installing')}{' '}
        <GetHelpButton
          kind="secondary"
          as="a"
          isExternal
          href={`${helpHotjarUrl}/articles/115009336727?itm_source=insights_installation_help`}
        >
          {t('activation:tracking-code.get-help')}
        </GetHelpButton>
      </Footer>
    </Modal>
  ) : null;
};
